import React, { useState, useEffect } from 'react';
import { indentAPI, storeAPI, itemAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { SpellCheckInput } from '../components/SpellCheckInput';
import { DataTable } from '../components/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileText,
  Plus,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Package,
  ArrowRight,
  Trash2,
  Eye,
  Send,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

export const IndentsPage = () => {
  const [loading, setLoading] = useState(true);
  const [indents, setIndents] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedIndent, setSelectedIndent] = useState(null);
  
  // Form states
  const [indentForm, setIndentForm] = useState({
    requesting_store_id: '',
    source_store_id: '',
    urgency: 'normal',
    notes: '',
    items: []
  });
  const [approvalItems, setApprovalItems] = useState([]);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Item selection for indent
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedItemQty, setSelectedItemQty] = useState('');
  const [selectedItemNotes, setSelectedItemNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [indentsRes, storesRes, itemsRes, dashboardRes] = await Promise.all([
        indentAPI.getAll(),
        storeAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        indentAPI.getDashboard().catch(() => ({ data: {} }))
      ]);
      
      setIndents(indentsRes.data);
      setStores(storesRes.data);
      setItems(itemsRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error('Failed to load indents');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIndent = async () => {
    if (!indentForm.requesting_store_id || !indentForm.source_store_id || indentForm.items.length === 0) {
      toast.error('Please select stores and add at least one item');
      return;
    }
    
    try {
      setSaving(true);
      await indentAPI.create({
        requesting_store_id: indentForm.requesting_store_id,
        source_store_id: indentForm.source_store_id,
        urgency: indentForm.urgency,
        notes: indentForm.notes || null,
        items: indentForm.items.map(item => ({
          item_id: item.item_id,
          requested_qty: item.requested_qty,
          notes: item.notes || null
        }))
      });
      
      toast.success('Indent created successfully');
      setCreateDialogOpen(false);
      setIndentForm({
        requesting_store_id: '',
        source_store_id: '',
        urgency: 'normal',
        notes: '',
        items: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create indent');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = () => {
    if (!selectedItemId || !selectedItemQty || parseFloat(selectedItemQty) <= 0) {
      toast.error('Please select an item and enter a valid quantity');
      return;
    }
    
    const item = items.find(i => i.id === selectedItemId);
    if (!item) return;
    
    // Check if item already exists
    if (indentForm.items.some(i => i.item_id === selectedItemId)) {
      toast.error('Item already added');
      return;
    }
    
    setIndentForm({
      ...indentForm,
      items: [...indentForm.items, {
        item_id: selectedItemId,
        item_name: item.name,
        unit: item.unit,
        requested_qty: parseFloat(selectedItemQty),
        notes: selectedItemNotes
      }]
    });
    
    setSelectedItemId('');
    setSelectedItemQty('');
    setSelectedItemNotes('');
  };

  const handleRemoveItem = (itemId) => {
    setIndentForm({
      ...indentForm,
      items: indentForm.items.filter(i => i.item_id !== itemId)
    });
  };

  const openApproveDialog = (indent) => {
    setSelectedIndent(indent);
    // Initialize approval items with requested quantities
    setApprovalItems(indent.items.map(item => ({
      ...item,
      approved_qty: item.requested_qty
    })));
    setApprovalNotes('');
    setApproveDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedIndent) return;
    
    // Validate at least one item has approved quantity
    const hasApproved = approvalItems.some(item => item.approved_qty > 0);
    if (!hasApproved) {
      toast.error('Please approve at least one item or reject the indent');
      return;
    }
    
    try {
      setSaving(true);
      await indentAPI.approve(selectedIndent.id, {
        items: approvalItems.map(item => ({
          item_id: item.item_id,
          approved_qty: parseFloat(item.approved_qty) || 0
        })),
        approval_notes: approvalNotes || null
      });
      
      toast.success('Indent approved and stock issued');
      setApproveDialogOpen(false);
      setSelectedIndent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve indent');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedIndent || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    try {
      setSaving(true);
      await indentAPI.reject(selectedIndent.id, {
        rejection_reason: rejectionReason
      });
      
      toast.success('Indent rejected');
      setRejectDialogOpen(false);
      setSelectedIndent(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject indent');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (indent) => {
    if (!confirm('Are you sure you want to cancel this indent?')) return;
    
    try {
      await indentAPI.cancel(indent.id);
      toast.success('Indent cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel indent');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'outline', icon: Clock, color: 'text-amber-600' },
      approved: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      partially_approved: { variant: 'secondary', icon: CheckCircle, color: 'text-blue-600' },
      fulfilled: { variant: 'default', icon: Package, color: 'text-green-700' },
      rejected: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      cancelled: { variant: 'outline', icon: XCircle, color: 'text-gray-500' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
      </Badge>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyConfig = {
      critical: { variant: 'destructive', className: 'bg-red-500' },
      urgent: { variant: 'default', className: 'bg-amber-500' },
      normal: { variant: 'outline', className: '' }
    };
    
    const config = urgencyConfig[urgency] || urgencyConfig.normal;
    
    return (
      <Badge variant={config.variant} className={config.className}>
        {urgency.charAt(0).toUpperCase() + urgency.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="indents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Indents / Requisitions</h1>
          <p className="text-muted-foreground mt-1">Request stock from stores</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-indent-btn">
          <Plus className="h-4 w-4 mr-2" />
          Raise Indent
        </Button>
      </div>

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className={dashboard.critical_count > 0 ? 'border-red-300 bg-red-50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <div>
                  <p className="text-xs text-muted-foreground">Critical</p>
                  <p className="text-2xl font-bold text-red-600">{dashboard.critical_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={dashboard.urgent_count > 0 ? 'border-amber-300 bg-amber-50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <div>
                  <p className="text-xs text-muted-foreground">Urgent</p>
                  <p className="text-2xl font-bold text-amber-600">{dashboard.urgent_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-xs text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold">{dashboard.pending_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-xs text-muted-foreground">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{dashboard.approved_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-emerald-500" />
                <div>
                  <p className="text-xs text-muted-foreground">Fulfilled</p>
                  <p className="text-2xl font-bold text-emerald-600">{dashboard.fulfilled_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-400" />
                <div>
                  <p className="text-xs text-muted-foreground">Rejected</p>
                  <p className="text-2xl font-bold text-red-500">{dashboard.rejected_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Indents Table with DataTable */}
      <Card>
        <CardHeader>
          <CardTitle>All Indents</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            module="indents"
            data={indents}
            loading={loading}
            title="Indents / Requisitions Report"
            columns={[
              { key: 'indent_number', label: 'Indent #', render: (val) => <span className="font-mono font-medium">{val}</span> },
              { key: 'requesting_store_name', label: 'From (Requesting)' },
              { key: 'source_store_name', label: 'To (Source)', render: (val) => (
                <div className="flex items-center gap-1">
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  {val}
                </div>
              )},
              { key: 'items', label: 'Items', render: (val) => <Badge variant="outline">{val?.length || 0} items</Badge>, sortable: false },
              { key: 'urgency', label: 'Urgency', render: (val) => getUrgencyBadge(val) },
              { key: 'status', label: 'Status', render: (val) => getStatusBadge(val) },
              { key: 'requested_by_name', label: 'Requested By' },
              { key: 'created_at', label: 'Date', render: (val) => formatDate(val) },
            ]}
            renderActions={(indent) => (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedIndent(indent);
                    setViewDialogOpen(true);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                {indent.status === 'pending' && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => openApproveDialog(indent)}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setSelectedIndent(indent);
                        setRejectDialogOpen(true);
                      }}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-500"
                      onClick={() => handleCancel(indent)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            )}
          />
        </CardContent>
      </Card>

      {/* Create Indent Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Raise New Indent
            </DialogTitle>
            <DialogDescription>
              Request stock items from a source store
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Store Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Requesting Store (Your Store) *</Label>
                <Select 
                  value={indentForm.requesting_store_id} 
                  onValueChange={(v) => setIndentForm({ ...indentForm, requesting_store_id: v })}
                >
                  <SelectTrigger data-testid="requesting-store-select">
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Source Store (Issue From) *</Label>
                <Select 
                  value={indentForm.source_store_id} 
                  onValueChange={(v) => setIndentForm({ ...indentForm, source_store_id: v })}
                >
                  <SelectTrigger data-testid="source-store-select">
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.filter(s => s.id !== indentForm.requesting_store_id).map(store => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Urgency */}
            <div className="space-y-2">
              <Label>Urgency Level</Label>
              <Select 
                value={indentForm.urgency} 
                onValueChange={(v) => setIndentForm({ ...indentForm, urgency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Add Items */}
            <div className="space-y-2">
              <Label>Add Items</Label>
              <div className="flex gap-2">
                <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                  <SelectTrigger className="flex-1" data-testid="item-select">
                    <SelectValue placeholder="Select item" />
                  </SelectTrigger>
                  <SelectContent>
                    {items.map(item => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder="Qty"
                  className="w-24"
                  value={selectedItemQty}
                  onChange={(e) => setSelectedItemQty(e.target.value)}
                  data-testid="item-qty-input"
                />
                <Input
                  placeholder="Notes (optional)"
                  className="w-40"
                  value={selectedItemNotes}
                  onChange={(e) => setSelectedItemNotes(e.target.value)}
                />
                <Button type="button" onClick={handleAddItem} data-testid="add-item-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Items List */}
            {indentForm.items.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {indentForm.items.map((item) => (
                      <TableRow key={item.item_id}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell>{item.requested_qty}</TableCell>
                        <TableCell>{item.unit}</TableCell>
                        <TableCell className="text-muted-foreground">{item.notes || '-'}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500"
                            onClick={() => handleRemoveItem(item.item_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes <span className="text-xs text-muted-foreground">(spell-check enabled)</span></Label>
              <SpellCheckInput
                value={indentForm.notes}
                onChange={(e) => setIndentForm({ ...indentForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                context="indent notes"
                multiline
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateIndent} disabled={saving} data-testid="submit-indent-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Submit Indent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Indent Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[900px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Indent Details - {selectedIndent?.indent_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedIndent && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Requesting Store</p>
                  <p className="font-medium">{selectedIndent.requesting_store_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Source Store</p>
                  <p className="font-medium">{selectedIndent.source_store_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Requested By</p>
                  <p className="font-medium">{selectedIndent.requested_by_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(selectedIndent.created_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  {getStatusBadge(selectedIndent.status)}
                </div>
                <div>
                  <p className="text-muted-foreground">Urgency</p>
                  {getUrgencyBadge(selectedIndent.urgency)}
                </div>
              </div>

              {selectedIndent.notes && (
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  <p className="text-sm">{selectedIndent.notes}</p>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Approved</TableHead>
                      <TableHead>Unit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedIndent.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell>{item.requested_qty}</TableCell>
                        <TableCell>
                          {item.approved_qty !== null ? (
                            <span className={item.approved_qty < item.requested_qty ? 'text-amber-600' : 'text-green-600'}>
                              {item.approved_qty}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{item.unit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {selectedIndent.approved_by_name && (
                <div className="text-sm text-muted-foreground">
                  Approved by {selectedIndent.approved_by_name} on {formatDate(selectedIndent.approved_at)}
                </div>
              )}
              
              {selectedIndent.stock_issue_number && (
                <div className="text-sm text-green-600">
                  Stock Issue: {selectedIndent.stock_issue_number}
                </div>
              )}
              
              {selectedIndent.rejection_reason && (
                <div className="text-sm text-red-600">
                  Rejection Reason: {selectedIndent.rejection_reason}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[900px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Approve Indent - {selectedIndent?.indent_number}
            </DialogTitle>
            <DialogDescription>
              Review and approve the requested quantities. You can partially approve items.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Approve Qty</TableHead>
                    <TableHead>Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvalItems.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.item_name}</TableCell>
                      <TableCell>{item.requested_qty}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          className="w-24"
                          value={item.approved_qty}
                          max={item.requested_qty}
                          min={0}
                          onChange={(e) => {
                            const newItems = [...approvalItems];
                            newItems[idx].approved_qty = parseFloat(e.target.value) || 0;
                            setApprovalItems(newItems);
                          }}
                        />
                      </TableCell>
                      <TableCell>{item.unit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-2">
              <Label>Approval Notes</Label>
              <SpellCheckInput
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Optional notes..."
                context="approval notes"
                multiline
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApprove} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Approve & Issue Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Reject Indent - {selectedIndent?.indent_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <SpellCheckInput
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                context="rejection reason"
                multiline
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reject Indent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IndentsPage;
